import { useCallback, useState } from 'react';

/**
 * The state behind the JSON escape hatch.
 *
 * Three things have to be kept for the hatch to be honest:
 *
 * - **what the form holds**, so opening the hatch shows the edit in progress rather than what
 *   was loaded;
 * - **the document it was built from**, so anything the form has no input for — an extension
 *   field, a link, a property we have never heard of — travels back out on save instead of
 *   being dropped by a round trip through the form;
 * - **a generation**, used as the form's `key`. Applying replaces the document wholesale, so
 *   the form is remounted on it rather than asked to reconcile field by field.
 */
export const useJsonHatch = <V>(initialValues: V, readDocument: (document: Record<string, unknown>) => V, initialBaseline?: unknown) => {
    const [values, setValues] = useState(initialValues);
    const [baseline, setBaseline] = useState<unknown>(initialBaseline);
    const [generation, setGeneration] = useState(0);

    const apply = useCallback(
        (document: Record<string, unknown>) => {
            setValues(readDocument(document));
            // The applied document becomes the base, so what the form cannot show is carried
            // from what was pasted rather than from what happened to be loaded first.
            setBaseline(document);
            setGeneration(previous => previous + 1);
        },
        [readDocument]
    );

    return { values, setValues, baseline, generation, apply };
};
